import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Pagination from '../../components/common/Pagination';
import StyledSelectDropdown from '../../components/common/StyledSelectDropdown';
import { FiBarChart2, FiChevronRight, FiChevronDown, FiUser } from 'react-icons/fi';
import { getDefaultItemsPerPageSync } from '../../utils/settings';
import { getProjectManagementPaths } from '../../modules/projectManagement/config';
import { canFeatureForRole, PERMISSIONS } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { notifyApiError } from '../../utils/notify';

const PRIMARY_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
const TEAMS_PURPLE = 'rgb(102, 126, 234)';

// Direct + indirect reports of `rootId` (walk the manager chain). Used to scope the
// By Member summary for users who lack the "View All Tasks" permission.
const computeSubordinateIds = (users, rootId) => {
  if (!rootId) return [];
  const result = [];
  const visited = new Set();
  const queue = [String(rootId)];
  while (queue.length) {
    const managerId = queue.shift();
    if (visited.has(managerId)) continue;
    visited.add(managerId);
    users.forEach((u) => {
      const uid = String(u._id || u.id || '');
      const mgr = String(u.manager?._id || u.manager?.id || u.manager || '');
      if (mgr === managerId && uid !== String(rootId)) {
        result.push(uid);
        queue.push(uid);
      }
    });
  }
  return result;
};

// Clickable task-code / project-name link inside the drill-down detail table.
const linkBtnStyle = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  color: '#667eea',
  fontWeight: 600,
  fontSize: 12,
  cursor: 'pointer',
  textDecoration: 'underline',
  whiteSpace: 'nowrap',
};

// Shared style for the report's right-corner filter checkboxes.
const checkboxLabelStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 34,
  fontSize: 12,
  fontWeight: 600,
  color: '#4a5568',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
};

// Status columns (order = left-to-right). Legacy 'Invalid' is folded into 'Invalid Issue'
// so the per-status columns always sum to the Total column.
const TASK_STATUSES = [
  'Not Started',
  'In Progress',
  'R&D Development',
  'R&D Testing',
  'Final Verification',
  'Complete',
  'Re-open',
  'On Hold',
  'Invalid Issue',
];
// Display labels for status values — 'Complete' is stored in DB but shown as 'Completed' in UI.
const STATUS_DISPLAY_LABEL = { Complete: 'Completed' };

// Fold a raw { status: count } map into the 8 display buckets and compute the total.
const buildDisplayCounts = (statusCounts) => {
  const counts = TASK_STATUSES.reduce((acc, status) => ({ ...acc, [status]: 0 }), {});
  Object.entries(statusCounts || {}).forEach(([status, n]) => {
    const key = status === 'Invalid' ? 'Invalid Issue' : status;
    if (counts[key] !== undefined) counts[key] += n;
  });
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  return { counts, total };
};

// Fold a task's raw status into a display bucket (legacy 'Invalid' → 'Invalid Issue').
const foldStatus = (status) => (status === 'Invalid' ? 'Invalid Issue' : status);

// Count a task list into the 8 display buckets + total.
const countTasksByStatus = (tasks) => {
  const counts = TASK_STATUSES.reduce((acc, status) => ({ ...acc, [status]: 0 }), {});
  (tasks || []).forEach((t) => {
    const key = foldStatus(t.status);
    if (counts[key] !== undefined) counts[key] += 1;
  });
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  return { counts, total };
};

// Group a member's tasks by project (standalone tasks → "No Project"), sorted by name A-Z.
const groupTasksByProject = (tasks) => {
  const groups = new Map();
  (tasks || []).forEach((t) => {
    const pid = t.projectId ? String(t.projectId) : '';
    if (!groups.has(pid)) {
      groups.set(pid, {
        projectId: pid,
        projectName: pid ? (t.project || '-') : '[No Project]',
        tasks: [],
      });
    }
    groups.get(pid).tasks.push(t);
  });
  return Array.from(groups.values()).sort((a, b) =>
    a.projectName.localeCompare(b.projectName)
  );
};

// Group a project's tasks by assignee (multi-assignee → counted under each). Tasks with
// no assignee are skipped. Sorted by username A-Z.
const groupTasksByUser = (tasks) => {
  const groups = new Map();
  (tasks || []).forEach((t) => {
    const assignees = Array.isArray(t.assigneesDetail) ? t.assigneesDetail : [];
    assignees.forEach((a) => {
      const uid = String(a?.userId || a?._id || a?.id || '');
      if (!uid) return;
      if (!groups.has(uid)) {
        groups.set(uid, { userId: uid, userName: a?.name || a?.email || 'Unknown', tasks: [] });
      }
      groups.get(uid).tasks.push(t);
    });
  });
  return Array.from(groups.values()).sort((a, b) =>
    String(a.userName || '').localeCompare(String(b.userName || ''))
  );
};

const SummaryReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const projectManagementPaths = getProjectManagementPaths(
    location.pathname.startsWith('/employee') ? 'employee' : 'admin'
  );

  // Current user + permission: decides how the By Member view is scoped.
  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const currentUserId = useMemo(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.id || u?._id || '').trim();
    } catch (_) {
      return '';
    }
  }, []);
  const currentUserRole = useMemo(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.role || '').trim().toLowerCase();
    } catch (_) {
      return '';
    }
  }, []);
  const hasViewAllTasks = useMemo(
    () => canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.VIEW_ALL_TASKS]),
    [currentUserRole, rolePermissions]
  );

  // 'member' = per user, 'project' = per project.
  const [mode, setMode] = useState('member');
  // Access: whether the user may view this report at all (View All Tasks OR has reports).
  const [accessChecked, setAccessChecked] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  // Candidate By Member id sets. 'all' = every user; 'team' = self + reports.
  const [allUserIds, setAllUserIds] = useState([]);
  const [myTeamIds, setMyTeamIds] = useState([]);
  // Scope chooser is offered only when the user has BOTH permission and reports.
  const [canChooseScope, setCanChooseScope] = useState(false);
  const [memberScope, setMemberScope] = useState('team'); // 'team' | 'all'
  // Drill-down: which row is expanded ('' = none), plus a per-row task cache + loading map.
  const [expandedKey, setExpandedKey] = useState('');
  const [detailCache, setDetailCache] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  // By Member Level-2: which project (+ optional status) inside the expanded member is
  // showing its tasks. Key: `${userId}::${projectId}::${status|'all'}` ('' = none).
  const [expandedSub, setExpandedSub] = useState('');
  const [memberData, setMemberData] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [projectData, setProjectData] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [memberLoaded, setMemberLoaded] = useState(false);
  const [projectLoaded, setProjectLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(getDefaultItemsPerPageSync());
  // Column sorting: 'name' (label col), a status name, or 'total'.
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  // By Member only: hide members whose 8 status columns are all zero — ON by default. The
  // By Member endpoint seeds a zeroed row for every requested user, so members with no tasks
  // always come back; By Project builds its rows from found tasks only, so a zero project row
  // can't exist there. The footer says how many rows this is holding back.
  const [hideZeroMembers, setHideZeroMembers] = useState(true);
  // Both groupings: finished projects are left out by default — a finished project's tasks
  // shouldn't pad the counts. Filtering happens server-side (counts AND drill-down), so the
  // member rows, the project rows under them, and the task lists always agree.
  const [showFinishedProjects, setShowFinishedProjects] = useState(false);

  // Once permissions are ready, resolve access + the allowed member set, then load By Member.
  useEffect(() => {
    if (!permissionsReady) return;
    resolveAccessAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsReady]);

  const resolveAccessAndLoad = async () => {
    try {
      setMemberLoading(true);
      const usersRes = await api.getUsers({ limit: 1000, isActive: 'true' });
      const users = usersRes?.success ? usersRes.data || [] : [];

      const everyone = users.map((u) => String(u._id || u.id || '')).filter(Boolean);
      const subs = computeSubordinateIds(users, currentUserId);
      const team = subs.length ? [String(currentUserId), ...subs].filter(Boolean) : [];

      // No permission and no reports → no access.
      if (!hasViewAllTasks && subs.length === 0) {
        setAccessDenied(true);
        setAccessChecked(true);
        return;
      }

      // Scope chooser only when the user has BOTH permission and reports.
      const chooser = hasViewAllTasks && subs.length > 0;
      // Default: 'team' — except a permission-only user (no reports) can only be 'all'.
      const initialScope = hasViewAllTasks && subs.length === 0 ? 'all' : 'team';
      const initialIds = initialScope === 'all' ? everyone : team;

      setAllUserIds(everyone);
      setMyTeamIds(team);
      setCanChooseScope(chooser);
      setMemberScope(initialScope);
      setAccessDenied(false);
      setAccessChecked(true);
      await loadMemberSummary(initialIds);
    } catch (error) {
      notifyApiError(error, 'Failed to load summary');
      setAccessChecked(true);
    } finally {
      setMemberLoading(false);
    }
  };

  // Project filters sent with every call. Trashed projects are always excluded (no checkbox —
  // Trash is Trash); finished ones only while the checkbox is off. `showFinishedArg` overrides
  // the state for the call that toggles the checkbox (state updates aren't visible yet at that
  // point) — same pattern as `scopeArg` below.
  const projectFilterParams = (showFinishedArg) => {
    const showFinished = showFinishedArg === undefined ? showFinishedProjects : showFinishedArg;
    return {
      excludeTrashedProjects: 'true',
      ...(showFinished ? {} : { excludeFinishedProjects: 'true' }),
    };
  };

  const loadMemberSummary = async (idsArg, showFinishedArg) => {
    try {
      setMemberLoading(true);
      const ids = idsArg || (memberScope === 'all' ? allUserIds : myTeamIds);
      const teamMemberIds = (ids || []).filter(Boolean).join(',');

      if (!teamMemberIds) {
        setMemberData([]);
        return;
      }

      const res = await api.getTaskSummaryByUser({
        teamMemberIds,
        ...projectFilterParams(showFinishedArg),
      });
      if (!res?.success) {
        toast.error('Failed to load summary');
        setMemberData([]);
        return;
      }
      const rows = (res.data || [])
        .slice()
        .sort((a, b) => String(a.userName || '').localeCompare(String(b.userName || '')));
      setMemberData(rows);
      setMemberLoaded(true);
    } catch (error) {
      notifyApiError(error, 'Failed to load summary');
    } finally {
      setMemberLoading(false);
    }
  };

  const loadProjectSummary = async (scopeArg, showFinishedArg) => {
    try {
      setProjectLoading(true);
      const useScope = scopeArg || memberScope;
      // 'team' scope → count only the team's tasks per project; 'all' → every task.
      const params = {
        ...(useScope === 'team' ? { teamMemberIds: (myTeamIds || []).join(',') } : {}),
        ...projectFilterParams(showFinishedArg),
      };
      const res = await api.getTaskSummaryByProject(params);
      if (!res?.success) {
        toast.error('Failed to load summary');
        setProjectData([]);
        return;
      }
      const rows = (res.data || []).slice().sort((a, b) => (b.total || 0) - (a.total || 0));
      setProjectData(rows);
      setProjectLoaded(true);
    } catch (error) {
      notifyApiError(error, 'Failed to load summary');
    } finally {
      setProjectLoading(false);
    }
  };

  const selectMode = (next) => {
    if (next === mode) return;
    setMode(next);
    setPage(1);
    if (next === 'project' && !projectLoaded) {
      loadProjectSummary();
    } else if (next === 'member' && !memberLoaded) {
      loadMemberSummary();
    }
  };

  // Switch the scope (All Users / My Team). Applies to BOTH groupings — reload the active
  // one now and invalidate the other so it reloads with the new scope when opened.
  const selectScope = (next) => {
    if (next === memberScope) return;
    setMemberScope(next);
    setPage(1);
    setExpandedKey('');
    setExpandedSub('');
    setDetailCache({});
    setMemberLoaded(false);
    setProjectLoaded(false);
    if (mode === 'project') {
      loadProjectSummary(next);
    } else {
      loadMemberSummary(next === 'all' ? allUserIds : myTeamIds);
    }
  };

  // Show/hide finished projects. Applies to BOTH groupings and to the cached drill-downs
  // (their task lists were fetched with the old flag), so everything is reset and reloaded.
  const toggleShowFinished = (next) => {
    setShowFinishedProjects(next);
    setPage(1);
    setExpandedKey('');
    setExpandedSub('');
    setDetailCache({});
    setMemberLoaded(false);
    setProjectLoaded(false);
    if (mode === 'project') {
      loadProjectSummary(undefined, next);
    } else {
      loadMemberSummary(undefined, next);
    }
  };

  // Fetch every task for a member/project (all pages) and cache it under `key`.
  const fetchDetail = async (key, params) => {
    try {
      setDetailLoading((prev) => ({ ...prev, [key]: true }));
      let pageNum = 1;
      let totalPagesLocal = 1;
      const all = [];
      do {
        const res = await api.getTasks({ ...params, page: pageNum, limit: 200 });
        if (!res?.success) break;
        all.push(...(res.data || []));
        totalPagesLocal = Math.max(1, res.totalPages || 1);
        pageNum += 1;
      } while (pageNum <= totalPagesLocal);
      setDetailCache((prev) => ({ ...prev, [key]: all }));
    } catch (error) {
      notifyApiError(error, 'Failed to load task details');
    } finally {
      setDetailLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Expand/collapse a summary row; fetch its tasks the first time it opens.
  const toggleRow = (rowId) => {
    const key = `${mode}:${rowId}`;
    setExpandedSub(''); // close any open Level-2 when the top row toggles
    if (expandedKey === key) {
      setExpandedKey('');
      return;
    }
    setExpandedKey(key);
    if (!detailCache[key]) {
      const params = {
        ...(mode === 'project'
          ? {
              projectId: rowId,
              // Match the scoped counts: in team scope, show only the team's tasks.
              ...(memberScope === 'team' ? { teamMemberIds: (myTeamIds || []).join(',') } : {}),
            }
          : { assigneeId: rowId }),
        // Keep the drill-down in step with the row's counts (By Member lists projects too).
        ...projectFilterParams(),
      };
      fetchDetail(key, params);
    }
  };

  // By Member Level-2: open/close a project's task list inside an expanded member.
  // `status` = 'all' (project name click) or a display status (status-cell click).
  const toggleSub = (userId, projectId, status) => {
    const key = `${userId}::${projectId}::${status || 'all'}`;
    setExpandedSub((prev) => (prev === key ? '' : key));
  };

  const activeData = mode === 'project' ? projectData : memberData;
  const activeLoading = mode === 'project' ? projectLoading : memberLoading;

  // Team-module style sort arrows: idle = up/down, active = up or down.
  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  // Rows actually shown. Filtering happens BEFORE sorting/pagination so the page count stays right.
  // total === 0 already means "all 8 status columns are zero" (see buildDisplayCounts).
  const visibleData = useMemo(() => {
    if (mode !== 'member' || !hideZeroMembers) return activeData;
    return activeData.filter((row) => buildDisplayCounts(row.statusCounts).total > 0);
  }, [activeData, mode, hideZeroMembers]);

  const sortedData = useMemo(() => {
    const arr = visibleData.slice();
    const dir = sortOrder === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      if (sortBy === 'name') {
        const av = mode === 'project' ? a.projectName : a.userName;
        const bv = mode === 'project' ? b.projectName : b.userName;
        return String(av || '').localeCompare(String(bv || '')) * dir;
      }
      const ac = buildDisplayCounts(a.statusCounts);
      const bc = buildDisplayCounts(b.statusCounts);
      const av = sortBy === 'total' ? ac.total : (ac.counts[sortBy] || 0);
      const bv = sortBy === 'total' ? bc.total : (bc.counts[sortBy] || 0);
      return (av - bv) * dir;
    });
    return arr;
  }, [visibleData, sortBy, sortOrder, mode]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / perPage));
  const pagedData = useMemo(
    () => sortedData.slice((page - 1) * perPage, page * perPage),
    [sortedData, page, perPage]
  );
  // How many members the zero filter is currently holding back — surfaced in the footer so the
  // entry count dropping (e.g. 63 → 41) doesn't read as missing data.
  const hiddenZeroCount = activeData.length - visibleData.length;

  // Keep the page in range if the row count / page size shrinks.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Collapse any open drill-down when the page or grouping changes.
  useEffect(() => {
    setExpandedKey('');
    setExpandedSub('');
  }, [page, mode]);

  // Level-2 task list (used inside a member's project group). No Project column
  // (already grouped by project). Task Code links to the task view page.
  const renderTaskListTable = (tasks) => (
    <div style={{ overflowX: 'auto', padding: '4px 0 6px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
        <thead>
          <tr style={{ background: '#eef2f7' }}>
            {['Task Code', 'Task Name', 'Status', 'Start Date', 'Due Date'].map((h) => (
              <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#4a5568', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <tr key={task.id} style={{ borderTop: '1px solid #edf2f7' }}>
              <td style={{ padding: '8px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>
                <button type="button" onClick={() => navigate(projectManagementPaths.taskView(task.id))} style={linkBtnStyle}>
                  {task.taskCode || '-'}
                </button>
              </td>
              <td style={{ padding: '8px 10px', fontSize: 12, color: '#4a5568' }}>{task.title || '-'}</td>
              <td style={{ padding: '8px 10px', fontSize: 12, color: '#4a5568', whiteSpace: 'nowrap' }}>{task.status || '-'}</td>
              <td style={{ padding: '8px 10px', fontSize: 12, color: '#718096', whiteSpace: 'nowrap' }}>{task.startDate || '-'}</td>
              <td style={{ padding: '8px 10px', fontSize: 12, color: '#718096', whiteSpace: 'nowrap' }}>{task.dueDate || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Level-1 (per member): project rows with status-wise counts of THAT member's tasks.
  // Project name click → all tasks; a status number click → only that status's tasks.
  // Member drill-down as REAL rows of the outer table, so the project columns line up
  // exactly under the member row's columns. Returns a fragment of <tr>s (Projects label
  // row + one row per project + an optional Level-2 task list under an opened project).
  const renderMemberRows = (userId, memberTasks) => {
    const groups = groupTasksByProject(memberTasks);
    const subPrefix = `${userId}::`;
    const colSpan = TASK_STATUSES.length + 2;
    return (
      <>
        <tr>
          <td
            colSpan={colSpan}
            style={{ background: '#eef2f7', padding: '6px 10px 6px 24px', fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', color: '#718096', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}
          >
            Projects
          </td>
        </tr>
        {groups.map((g) => {
          const { counts, total } = countTasksByStatus(g.tasks);
          const openStatus =
            expandedSub.startsWith(`${subPrefix}${g.projectId}::`)
              ? expandedSub.slice(`${subPrefix}${g.projectId}::`.length)
              : null;
          const subOpen = openStatus !== null;
          const subTasks = !subOpen
            ? []
            : openStatus === 'all'
              ? g.tasks
              : g.tasks.filter((t) => foldStatus(t.status) === openStatus);
          return (
            <React.Fragment key={g.projectId || '__none__'}>
              <tr style={{ borderBottom: '1px solid #e2e8f0', background: subOpen ? '#f7fafc' : 'transparent' }}>
                <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    onClick={() => toggleSub(userId, g.projectId, 'all')}
                    title="Click to see all tasks of this project"
                    style={{ background: 'none', border: 'none', padding: '0 0 0 18px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, color: '#344054', fontWeight: 600, fontSize: 12 }}
                  >
                    {subOpen ? <FiChevronDown size={13} aria-hidden /> : <FiChevronRight size={13} aria-hidden />}
                    {g.projectName}
                  </button>
                </td>
                {TASK_STATUSES.map((s) => {
                  const n = counts[s] || 0;
                  const clickable = n > 0;
                  return (
                    <td
                      key={s}
                      onClick={clickable ? () => toggleSub(userId, g.projectId, s) : undefined}
                      title={clickable ? `Show ${s} tasks` : undefined}
                      style={{ textAlign: 'center', padding: '9px 10px', fontSize: 13, color: '#4a5568', cursor: clickable ? 'pointer' : 'default', background: openStatus === s ? '#e6ecfb' : 'transparent' }}
                    >
                      <span style={{ textDecoration: clickable ? 'underline' : 'none', textUnderlineOffset: 2 }}>{n}</span>
                    </td>
                  );
                })}
                <td style={{ textAlign: 'center', padding: '9px 10px', fontSize: 13, color: '#4a5568', fontWeight: 600 }}>{total}</td>
              </tr>
              {subOpen && (
                <tr>
                  <td colSpan={colSpan} style={{ padding: '0 10px 10px 28px', background: '#fdfdfe', borderBottom: '1px solid #e2e8f0' }}>
                    {subTasks.length === 0 ? (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#718096', fontSize: 12 }}>No tasks found</div>
                    ) : (
                      renderTaskListTable(subTasks)
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  // By Project drill-down as REAL rows: one row per user in the project (status-wise
  // counts of THAT user's tasks here), aligned to the outer columns. Multi-assignee tasks
  // count under each assignee; unassigned tasks are skipped. Level-2 = that user's tasks.
  const renderProjectRows = (projectId, projectTasks) => {
    const groups = groupTasksByUser(projectTasks);
    const subPrefix = `${projectId}::`;
    const colSpan = TASK_STATUSES.length + 2;
    if (groups.length === 0) {
      return (
        <tr>
          <td colSpan={colSpan} style={{ padding: '14px', textAlign: 'center', color: '#718096', fontSize: 12, background: '#f7fafc', borderBottom: '1px solid #e2e8f0' }}>
            No assigned users
          </td>
        </tr>
      );
    }
    return (
      <>
        <tr>
          <td
            colSpan={colSpan}
            style={{ background: '#eef2f7', padding: '6px 10px 6px 24px', fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', color: '#718096', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}
          >
            Users
          </td>
        </tr>
        {groups.map((g) => {
          const { counts, total } = countTasksByStatus(g.tasks);
          const openStatus =
            expandedSub.startsWith(`${subPrefix}${g.userId}::`)
              ? expandedSub.slice(`${subPrefix}${g.userId}::`.length)
              : null;
          const subOpen = openStatus !== null;
          const subTasks = !subOpen
            ? []
            : openStatus === 'all'
              ? g.tasks
              : g.tasks.filter((t) => foldStatus(t.status) === openStatus);
          return (
            <React.Fragment key={g.userId}>
              <tr style={{ borderBottom: '1px solid #e2e8f0', background: subOpen ? '#f7fafc' : 'transparent' }}>
                <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    onClick={() => toggleSub(projectId, g.userId, 'all')}
                    title="Click to see all tasks of this user"
                    style={{ background: 'none', border: 'none', padding: '0 0 0 18px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, color: '#344054', fontWeight: 600, fontSize: 12 }}
                  >
                    {subOpen ? <FiChevronDown size={13} aria-hidden /> : <FiChevronRight size={13} aria-hidden />}
                    {g.userName}
                  </button>
                </td>
                {TASK_STATUSES.map((s) => {
                  const n = counts[s] || 0;
                  const clickable = n > 0;
                  return (
                    <td
                      key={s}
                      onClick={clickable ? () => toggleSub(projectId, g.userId, s) : undefined}
                      title={clickable ? `Show ${s} tasks` : undefined}
                      style={{ textAlign: 'center', padding: '9px 10px', fontSize: 13, color: '#4a5568', cursor: clickable ? 'pointer' : 'default', background: openStatus === s ? '#e6ecfb' : 'transparent' }}
                    >
                      <span style={{ textDecoration: clickable ? 'underline' : 'none', textUnderlineOffset: 2 }}>{n}</span>
                    </td>
                  );
                })}
                <td style={{ textAlign: 'center', padding: '9px 10px', fontSize: 13, color: '#4a5568', fontWeight: 600 }}>{total}</td>
              </tr>
              {subOpen && (
                <tr>
                  <td colSpan={colSpan} style={{ padding: '0 10px 10px 28px', background: '#fdfdfe', borderBottom: '1px solid #e2e8f0' }}>
                    {subTasks.length === 0 ? (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#718096', fontSize: 12 }}>No tasks found</div>
                    ) : (
                      renderTaskListTable(subTasks)
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  return (
    <div style={{ padding: '16px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 16px', color: '#2d3748', display: 'flex', alignItems: 'center', gap: 10 }}>
        <FiBarChart2 size={22} aria-hidden />
        Summary Report
      </h1>

      {!accessChecked ? (
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '40px', textAlign: 'center', color: '#718096', fontSize: 14 }}>
          Loading...
        </div>
      ) : accessDenied ? (
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '40px', textAlign: 'center', color: '#718096', fontSize: 14 }}>
          You don't have access to this report.
        </div>
      ) : (
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: '#344054' }}>
            {mode === 'project' ? 'Task Summary by Project' : 'Task Summary by Member'}
          </h3>
          {/* Left controls: scope dropdown (only when the user has both permission + reports) + grouping toggle */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {canChooseScope && (
              <div style={{ width: 160, minWidth: 0, order: 1 }}>
                <StyledSelectDropdown
                  ariaLabel="Report scope"
                  value={memberScope}
                  onChange={selectScope}
                  omitEmptyOption
                  options={[
                    { value: 'team', label: 'My Team' },
                    { value: 'all', label: 'All Users' },
                  ]}
                  leftIcon={<FiUser size={14} style={{ color: TEAMS_PURPLE, flexShrink: 0 }} aria-hidden />}
                />
              </div>
            )}
            {/* By Member / By Project toggle */}
            <div style={{ display: 'inline-flex', height: 34, border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
              {[
                { key: 'member', label: 'By Member' },
                { key: 'project', label: 'By Project' },
              ].map((opt) => {
                const active = mode === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => selectMode(opt.key)}
                    style={{
                      border: 'none',
                      height: '100%',
                      padding: '0 14px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      color: active ? '#ffffff' : '#4a5568',
                      background: active ? PRIMARY_GRADIENT : '#ffffff',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Right corner filters. */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 16, marginLeft: 'auto', flexWrap: 'wrap' }}>
            {/* Both groupings — off by default, so a finished project's tasks stay out of the counts. */}
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={showFinishedProjects}
                onChange={(e) => toggleShowFinished(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Include completed projects
            </label>
            {/* By Member only — By Project rows are built from found tasks, so they can never be all-zero. */}
            {mode === 'member' && (
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={hideZeroMembers}
                  onChange={(e) => {
                    setHideZeroMembers(e.target.checked);
                    setPage(1);
                  }}
                  style={{ cursor: 'pointer' }}
                />
                Hide members with no tasks
              </label>
            )}
          </div>
        </div>

        {activeLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
            <p style={{ margin: 0, fontSize: '14px' }}>Loading summary...</p>
          </div>
        ) : visibleData.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
            <p style={{ margin: 0, fontSize: '14px' }}>
              {mode === 'project'
                ? 'No task data available for any project'
                : hideZeroMembers && activeData.length > 0
                  ? 'Every member in this scope has zero tasks. Uncheck "Hide members with no tasks" to see them.'
                  : 'No task data available for any member'}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: PRIMARY_GRADIENT }}>
                <tr>
                  <th
                    onClick={() => handleSort('name')}
                    style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {mode === 'project' ? 'Project' : 'Username'}
                      <span style={{ color: sortBy === 'name' ? '#e9d5ff' : 'rgba(255,255,255,0.65)', fontSize: 10 }}>{getSortIcon('name')}</span>
                    </span>
                  </th>
                  {TASK_STATUSES.map((status) => (
                    <th
                      key={status}
                      onClick={() => handleSort(status)}
                      style={{ textAlign: 'center', padding: '10px', fontSize: '10px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        {STATUS_DISPLAY_LABEL[status] || status}
                        <span style={{ color: sortBy === status ? '#e9d5ff' : 'rgba(255,255,255,0.65)', fontSize: 9 }}>{getSortIcon(status)}</span>
                      </span>
                    </th>
                  ))}
                  <th
                    onClick={() => handleSort('total')}
                    style={{ textAlign: 'center', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      Total
                      <span style={{ color: sortBy === 'total' ? '#e9d5ff' : 'rgba(255,255,255,0.65)', fontSize: 10 }}>{getSortIcon('total')}</span>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pagedData.map((row) => {
                  const { counts, total } = buildDisplayCounts(row.statusCounts);
                  const rowId = mode === 'project' ? row.projectId : row.userId;
                  const rowLabel = mode === 'project' ? row.projectName : row.userName;
                  const key = `${mode}:${rowId}`;
                  const expanded = expandedKey === key;
                  const detailRows = detailCache[key] || [];
                  const detailBusy = Boolean(detailLoading[key]);
                  return (
                    <React.Fragment key={key}>
                      <tr
                        onClick={() => toggleRow(rowId)}
                        style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer', background: expanded ? '#f7fafc' : 'transparent' }}
                        title="Click to see tasks"
                      >
                        <td style={{ padding: '10px', whiteSpace: 'nowrap', color: '#4a5568', fontSize: '13px', fontWeight: 600 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {expanded ? <FiChevronDown size={14} aria-hidden /> : <FiChevronRight size={14} aria-hidden />}
                            {rowLabel}
                          </span>
                        </td>
                        {TASK_STATUSES.map((status) => (
                          <td key={status} style={{ textAlign: 'center', padding: '10px', color: '#4a5568', fontSize: '13px' }}>
                            {counts[status] || 0}
                          </td>
                        ))}
                        <td style={{ textAlign: 'center', padding: '10px', color: '#4a5568', fontSize: '13px', fontWeight: 600 }}>
                          {total}
                        </td>
                      </tr>
                      {expanded && (
                        detailBusy ? (
                          <tr>
                            <td colSpan={TASK_STATUSES.length + 2} style={{ padding: '16px', textAlign: 'center', color: '#718096', fontSize: 13, background: '#f7fafc', borderBottom: '1px solid #e2e8f0' }}>Loading tasks...</td>
                          </tr>
                        ) : detailRows.length === 0 ? (
                          <tr>
                            <td colSpan={TASK_STATUSES.length + 2} style={{ padding: '16px', textAlign: 'center', color: '#718096', fontSize: 13, background: '#f7fafc', borderBottom: '1px solid #e2e8f0' }}>No tasks found</td>
                          </tr>
                        ) : mode === 'member' ? (
                          renderMemberRows(rowId, detailRows)
                        ) : (
                          renderProjectRows(rowId, detailRows)
                        )
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!activeLoading && visibleData.length > 0 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={visibleData.length}
            itemsPerPage={perPage}
            onPageChange={setPage}
            onItemsPerPageChange={(next) => {
              setPerPage(next);
              setPage(1);
            }}
            extraEnd={
              hiddenZeroCount > 0 ? (
                <span style={{ fontSize: 12, color: '#718096', whiteSpace: 'nowrap' }}>
                  {hiddenZeroCount} member{hiddenZeroCount > 1 ? 's' : ''} with no tasks hidden
                </span>
              ) : null
            }
          />
        )}
      </div>
      )}
    </div>
  );
};

export default SummaryReportPage;
